import React, { useEffect, useState } from 'react';

import cx from '../../utils/classnames';

import styles from './circleScore.module.scss';

import type { ReactNode } from 'react';

type Props = {
  className?: string;
  /** Custom className for circle icon */
  circleIconClassName?: string;
  /** Custom className for label */
  scoreClassName?: string;
  /** Custom className for circle */
  circleClassName?: string;
  /** Flag to enable animation when component mounted */
  hasAnimation?: boolean;
  /** Number of max score value */
  max?: number;
  /** Number of score value */
  score: number | null;
  /** Number of circle size in pixel */
  size?: number;
  /** Number of circle stoke width in pixel */
  strokeWidth?: number;
  /** Custom label of the circle score */
  label?: ReactNode;
  /** Spacing between CircleScore and label in pixel */
  spaceBetween?: number;
};

function CircleScore(props: Props) {
  // TODO QA#1: Anti-clockwise circle animation
  const {
    className,
    circleIconClassName,
    scoreClassName,
    circleClassName,
    score,
    max = 5,
    size = 55,
    strokeWidth = 4,
    hasAnimation = true,
    spaceBetween,
    label = '',
    ...rest
  } = props;

  // cal actual radius, circumference and position of circle
  const axis = size / 2;
  const radius = axis - strokeWidth;
  const circumference = radius * 2 * Math.PI;

  const isNullScore = score === null;
  const displayedScore = Math.max(Math.min(max, score || 0), 0);
  const animationClassName = hasAnimation ? styles.animated : '';

  const circleDefaultAttr = {
    r: radius,
    cx: axis,
    cy: axis,
    fill: 'transparent',
    strokeWidth,
  };

  // initial with no progress rendered (circumference = strokeOffset)
  const [dashOffset, setDashOffset] = useState(circumference);

  useEffect(() => {
    if (!isNullScore) {
      // to animate the circle animation
      const percent = (displayedScore * 100) / max;
      setDashOffset(circumference - (percent * circumference) / 100);
    }
  }, [displayedScore, max, circumference, isNullScore]);

  const customLabelStyle = label && spaceBetween && { style: { marginLeft: spaceBetween } };

  return (
    <div data-testid="circleScore-wrapper" className={cx(className, styles.container)}>
      <div
        className={cx(circleIconClassName, styles.wrapper)}
        style={{ width: size, height: size, minWidth: size }}
        {...rest}
      >
        <div className={cx(scoreClassName, styles.scoreBox, isNullScore && styles.nullScore)}>
          {isNullScore ? '-' : displayedScore}
        </div>
        <div className={styles.svgBox}>
          <svg height={size} width={size}>
            <circle className={styles.circle} {...circleDefaultAttr} />
            <circle
              className={cx(
                circleClassName,
                styles.circle,
                !isNullScore && styles.progress,
                animationClassName,
              )}
              strokeDashoffset={dashOffset}
              strokeDasharray={circumference}
              {...circleDefaultAttr}
            />
          </svg>
        </div>
      </div>
      {label && (
        <span className={styles.label} {...customLabelStyle}>
          {label}
        </span>
      )}
    </div>
  );
}

export default CircleScore;
